import React, { useState } from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Avatar, Tooltip, Menu, MenuItem, ListItemIcon, Divider } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AppsIcon from '@mui/icons-material/Apps';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useSession } from '../../../core/context/SessionContext';
import { useAuth } from 'react-oidc-context';
import { useCart } from '../../../modules/venta/application/context/CartContext';

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { terminal, equipo, clearSession } = useSession();
  const { limpiarCarrito } = useCart();
  const auth = useAuth();
  
  // Estado para el menú desplegable del perfil
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    limpiarCarrito(); // Limpia el carrito de compras y localStorage
    clearSession(); // Limpia la terminal y equipo del localStorage
    auth.signoutRedirect(); // Cierra sesión en Keycloak y redirige al login
  };

  // Obtener datos reales del usuario desde Keycloak, o usar valores por defecto
  const userName = auth.user?.profile?.name ?? auth.user?.profile?.preferred_username ?? 'Usuario';
  const userInitials = userName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase();

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: '#ffffff', color: '#333', boxShadow: 1 }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <img src="/logo-sigo.svg" alt="SIGOO" style={{ height: '24px', marginRight: '8px' }} />
            <span style={{ fontSize: '1rem', color: '#666', fontWeight: 'normal' }}>Droop</span>
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" sx={{ color: '#0066cc', fontWeight: 500 }}>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {terminal && equipo 
                ? `${terminal.nombre_terminal} | Equipo ${equipo.codigo_equipo}`
                : 'No se encontraron terminales'}
            </Typography>
          </Box>
          
          <Tooltip title="Opciones de cuenta">
            <IconButton
              onClick={handleProfileClick}
              size="small"
              sx={{ ml: 1, p: 0 }}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <Avatar sx={{ bgcolor: '#e0e0e0', color: '#333', width: 36, height: 36 }}>
                {userInitials}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Menú desplegable del perfil */}
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{ paper: {
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                minWidth: 200,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleClose}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Mi Perfil
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              Cerrar Sesión
            </MenuItem>
          </Menu>
          
          <IconButton color="inherit">
            <AppsIcon sx={{ color: '#666' }} />
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
